import React, { useState } from "react";
import {
  Edit,
  FunctionField,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  useTranslate,
  useUpdate
} from "react-admin";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { EditButtonIcon, LogDetailsIcon } from "assets/Icons";
import { CustomButton } from "./CustomButton";
import dayjs from "dayjs";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import { getZonedDate } from "../utils/helpers";

const AppointmentShowLog = ({ appointmentId }: { appointmentId?: string }) => {
  const translate = useTranslate();
  const [isNoteEdit, setIsNoteEdit] = useState<boolean>(false);
  const [edit] = useUpdate();
  const submit = (data: any) => {
    edit(
      "Appointment",
      {
        id: appointmentId,
        data: { notes: data.notes || "" }
      },
      {
        onSuccess: () => {
          setIsNoteEdit(false);
        }
      }
    );
  };
  return (
    <div className="h-min xl:col-span-1 2xl:col-span-1 bg-mono-primaryLightBgColor rounded-[8px]">
      <SimpleShowLayout>
        <ShowAndEditDetails
          title={translate("appointment.log")}
          view="show"
          icon={<LogDetailsIcon />}
          customButtons={
            <CustomButton
              text={translate("appointment.notes")}
              cyId="notes-button"
              variant="secondary"
              startIcon={isNoteEdit ? <CloseIcon /> : <EditButtonIcon />}
              onClick={() => setIsNoteEdit((prev) => !prev)}
            />
          }
          marginBottom="0px"
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.checked_in_at")}:
              </div>
              <div
                data-cy="check-in-display"
                className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']"
              >
                {record?.checked_in_at &&
                dayjs(
                  getZonedDate(
                    record?.checked_in_at,
                    record?.location?.timezone
                  )
                ).isAfter("2023-07-12")
                  ? dayjs(
                      getZonedDate(
                        record?.checked_in_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.confirmed_at")}:
              </div>
              <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']">
                {record?.confirmed_at
                  ? dayjs(
                      getZonedDate(
                        record?.confirmed_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.completed_at")}:
              </div>
              <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']">
                {record?.completed_at
                  ? dayjs(
                      getZonedDate(
                        record?.completed_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.rescheduled_at")}:
              </div>
              <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']">
                {record?.rescheduled_at
                  ? dayjs(
                      getZonedDate(
                        record?.rescheduled_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.cancelled_at")}:
              </div>
              <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']">
                {record?.cancelled_at
                  ? dayjs(
                      getZonedDate(
                        record?.cancelled_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={false}
          render={(record: any) => (
            <div className="flex justify-between items-center gap-[5px] flex-wrap">
              <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                {translate("appointment.moved_at")}:
              </div>
              <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter']">
                {record?.manually_edited_at
                  ? dayjs(
                      getZonedDate(
                        record?.manually_edited_at,
                        record?.location?.timezone
                      )
                    ).format("M/D/YYYY, hh:mmA")
                  : "-"}
              </div>
            </div>
          )}
        />
        {!isNoteEdit && (
          <FunctionField
            label={false}
            data-cy="notes-display"
            render={(record: any) => (
              <>
                {record?.notes && (
                  <div className="border-t border-[rgba(48,47,46,0.15)] pt-[8px]">
                    <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
                      {translate("appointment.notes")}:
                    </div>
                    <div className="text-[14px] text-[rgba(0,0,0,0.87)] font-['Inter'] mt-[6px]">
                      {record?.notes}
                    </div>
                  </div>
                )}
              </>
            )}
          />
        )}
        {isNoteEdit && (
          <Edit
            id={appointmentId}
            resource="Appointment"
            actions={false}
            sx={{
              "& .RaToolbar-desktopToolbar": {
                gap: "8px",
                minHeight: "40px",
                padding: "0px 16px 16px 16px"
              },
              "& .RaEdit-noActions": {
                marginTop: 0
              }
            }}
          >
            <SimpleForm
              resolver={yupResolver(validationSchema)}
              onSubmit={submit}
              toolbar={<div />}
              sx={{ padding: "0px" }}
            >
              <TextInput
                source="notes"
                InputProps={{ inputProps: { "data-cy": "notes-input" } }}
                label={translate("appointment.notes")}
                fullWidth
                multiline
              />
              <div className="w-full flex justify-end mb-[6px]">
                <CustomButton
                  cyId="save-note-button"
                  text="Save"
                  type="submit"
                />
              </div>
            </SimpleForm>
          </Edit>
        )}
      </SimpleShowLayout>
    </div>
  );
};

export default AppointmentShowLog;

const validationSchema = yup
  .object()
  .shape({
    notes: yup.string().nullable()
  })
  .required();
